import React, { useState } from 'react'
import { Loader2, SquareArrowOutUpRight } from 'lucide-react'

import { ScheduleIcon } from '@/components/icons/schedule-icon'
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card'
import { Button } from '@/components/ui/button'
import { useScheduleQuery } from '@/hooks/api/use-schedule-query'
import { ScheduleEditDialog } from '../schedule/_components/edit-dialog'
import { getFrequencyFromSeconds } from '@/hooks/api/use-create-schedule-mutation'
import { getHumanizedInfoFromFrequency } from '../schedule/_utils/helper'

type ScheduleCardProps = {
  scheduleId?: string
}

function ScheduleCard({ scheduleId }: ScheduleCardProps) {
  const [open, setOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  const { data: scheduleData, isPending: isFetchingScheduleData } = useScheduleQuery({
    scheduleId: scheduleId || '',
    queryOptions: {
      enabled: !!scheduleId && open,
    },
  })

  if (!scheduleId) return null

  const activityStatus = scheduleData && scheduleData?.status === 'active' ? 'Active' : 'Paused'
  const dotColor =
    scheduleData && scheduleData?.status === 'active' ? 'bg-success-200' : 'bg-grape-200'
  return (
    <>
      <HoverCard open={open} onOpenChange={setOpen}>
        <HoverCardTrigger asChild>
          <Button unstyled className='flex w-full items-center justify-center pr-6'>
            <ScheduleIcon className='size-5 text-neutral-150' />
          </Button>
        </HoverCardTrigger>
        <HoverCardContent
          align='end'
          side='bottom'
          className='w-[200px] rounded-lg border border-neutral-white bg-gray-50 p-2 shadow-sidebar'
        >
          {isFetchingScheduleData ? (
            <div className='flex items-center justify-center py-6'>
              <Loader2 className='size-5 animate-spin text-neutral-150' />
            </div>
          ) : (
            <>
              <div className='border-b border-neutral-75 py-2'>
                <span className='flex items-center gap-2'>
                  <span className={`h-3 w-3 rounded-full ${dotColor}`} />
                  <span
                    className={
                      scheduleData?.status === 'paused'
                        ? 'text-muted-foreground'
                        : 'text-foreground text-xs font-medium text-neutral-900'
                    }
                  >
                    {activityStatus}
                  </span>
                </span>
              </div>
              <div className='flex items-center gap-1 self-stretch px-0 py-4 pl-4'>
                <p className='text-xs font-normal text-neutral-900'>
                  {scheduleData?.frequency && scheduleData?.start_time
                    ? getHumanizedInfoFromFrequency(
                        scheduleData?.frequency,
                        scheduleData?.start_time,
                      )
                    : 'No schedule'}
                </p>
              </div>
              <div className='flex items-center justify-end py-3'>
                <Button
                  unstyled
                  onClick={() => setIsEditDialogOpen((prevState) => !prevState)}
                  className='flex cursor-pointer items-center gap-2'
                >
                  <p className='text-xs font-light text-neutral-500 underline'>View and Edit</p>
                  <SquareArrowOutUpRight className='text-neutral-150' size={12} />
                </Button>
              </div>
            </>
          )}
        </HoverCardContent>
      </HoverCard>
      {isEditDialogOpen && (
        <ScheduleEditDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          scheduleData={{
            id: scheduleData?.id || '',
            agent_id: scheduleData?.agent_id || '',
            endDate: scheduleData?.end_time ? new Date(scheduleData?.end_time) : undefined,
            frequency: scheduleData?.frequency
              ? getFrequencyFromSeconds(scheduleData?.frequency)
              : 'daily',
            startDate: scheduleData?.start_time ? new Date(scheduleData.start_time) : new Date(),
            tool_id: scheduleData?.tool_id || '',
            tool_args: scheduleData?.tool_args,
            time: '',
          }}
        />
      )}
    </>
  )
}

export default ScheduleCard
